import {
  commentAuthorBadgeClass,
  commentAuthorInfoClass,
  commentAuthorNameClass,
  commentAvatarClass,
  commentCardClass,
  commentContentClass,
  commentDateClass,
  commentDeleteButtonClass,
  commentEmptyClass,
  commentErrorClass,
  commentFollowLinkClass,
  commentFooterActionsClass,
  commentFooterClass,
  commentFormClass,
  commentHeaderClass,
  commentPrivateBadgeClass,
  commentPrivateCheckboxLabelClass,
  commentPrivateDescriptionClass,
  commentPrivateHeaderClass,
  commentPrivateThreadClass,
  commentPublicationLinkClass,
  commentReplyClass,
  commentSubmitButtonClass,
  commentSubscribedLabelClass,
  commentsContainerClass,
  commentsTitleClass,
  commentTextareaClass,
  commentThreadClass,
  commentTranslateButtonClass,
  replyButtonClass,
  replyButtonIndentedClass,
} from "@repo/fika-blog/src/comments.css"
import { getDefaultAvatar } from "@repo/fika-design-system/avatar"
import { cdnClass } from "@repo/fika-design-system/cdn-class"
import {
  getLanguageLabel,
  getLocaleDisplayName,
} from "@repo/fika-shared/src/locale.js"
import { useLocation } from "@solidjs/router"
import { createQuery } from "@urql/solid"
import { createMemo, createSignal, For, Show } from "solid-js"
import { Icon } from "@/designSystem/components/icon"
import { RichContent } from "@/designSystem/patterns/rich-content"
import { createFragment, graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { displayName } from "@/lib/display-name"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { useRelativeTime } from "@/lib/use-relative-time"

const CommentMessageFragment = graphql(`
  fragment CommentMessage on Message {
    id
    content
    locale
    createdAt
    author {
      id
      isMe
      authorName
      title
      username
      email
      profileImage
      publicationUrl
      publication {
        id
        sub {
          id
        }
      }
    }
  }
`)

const CommentThreadFragment = graphql(
  `fragment CommentThread on Thread {
    id
    createdAt
    private
    messages {
      id
      ...CommentMessage
    }
  }`,
  [CommentMessageFragment],
)

const PostCommentsQuery = graphql(
  `query PostComments($postId: String!) {
    publishedPost(id: $postId) {
      id
      publication {
        id
        author {
          id
        }
      }
      comments {
        id
        ...CommentThread
      }
    }
  }`,
  [CommentThreadFragment],
)

const TranslateMessageMutation = graphql(
  `mutation TranslateCommentMessage($messageId: String!, $toLocale: Locale!) {
    translateMessage(messageId: $messageId, toLocale: $toLocale) {
      ... on MutationTranslateMessageSuccess {
        data {
          translatedContent
        }
      }
      ... on BaseError {
        ...ErrorFragment
      }
    }
  }`,
  [ErrorFragment],
)

const SendMessageMutation = graphql(
  `mutation SendCommentMessage($postId: String, $threadId: String, $content: String!, $format: MessageFormat, $private: Boolean, $locale: Locale) {
    sendMessage(postId: $postId, threadId: $threadId, content: $content, format: $format, private: $private, locale: $locale) {
      ... on MutationSendMessageSuccess {
        data {
          id
          post {
            id
            comments {
              id
              ...CommentThread
            }
          }
        }
      }
      ... on FlaggedMessageError {
        ...ErrorFragment
      }
    }
  }`,
  [CommentThreadFragment, ErrorFragment],
)

interface PostCommentsProps {
  postId: string
}

export function PostComments(props: PostCommentsProps) {
  const { LL, locale } = useI18nContext()
  const [commentText, setCommentText] = createSignal("")
  const [sendAsPrivate, setSendAsPrivate] = createSignal(false)
  const [replyingTo, setReplyingTo] = createSignal<string | null>(null)
  const [replyText, setReplyText] = createSignal("")

  const [result, reexecuteComments] = createQuery({
    query: PostCommentsQuery,
    variables: () => ({ postId: props.postId }),
  })

  const [createResult, sendMessage] = createMutationWithErrorHandling(
    SendMessageMutation,
    "sendMessage",
  )
  const [replyResult, sendReply] = createMutationWithErrorHandling(
    SendMessageMutation,
    "sendMessage",
  )

  const threads = createMemo(() => result.data?.publishedPost?.comments ?? [])
  const postAuthorId = createMemo(
    () => result.data?.publishedPost?.publication?.author?.id ?? null,
  )
  const [commentError, setCommentError] = createSignal<string | null>(null)
  const [replyError, setReplyError] = createSignal<string | null>(null)

  async function handleSubmitComment() {
    const text = commentText().trim()
    if (!text) return
    setCommentError(null)

    const isPrivate = sendAsPrivate()
    const out = await sendMessage(
      {
        postId: props.postId,
        content: text,
        format: "markdown",
        private: isPrivate || undefined,
        locale: locale(),
      },
      { muteGraphQlUnionToast: true },
    )

    if (out.success) {
      setCommentText("")
      setSendAsPrivate(false)
    } else if (out.error.message === "FLAGGED_MESSAGE") {
      setCommentError(LL().comments.flaggedMessage())
    }
  }

  async function handleSubmitReply(threadId: string) {
    const text = replyText().trim()
    if (!text) return
    setReplyError(null)

    const out = await sendReply(
      { threadId, content: text, format: "markdown", locale: locale() },
      { muteGraphQlUnionToast: true },
    )

    if (out.success) {
      setReplyText("")
      setReplyingTo(null)
    } else if (out.error.message === "FLAGGED_MESSAGE") {
      setReplyError(LL().comments.flaggedMessage())
    }
  }

  return (
    <div class={commentsContainerClass}>
      <h3 class={commentsTitleClass}>{LL().comments.title()}</h3>

      <div class={commentFormClass}>
        <textarea
          class={commentTextareaClass}
          placeholder={LL().comments.writeComment()}
          rows="3"
          value={commentText()}
          onInput={e => setCommentText(e.currentTarget.value)}
        />
        <label class={commentPrivateCheckboxLabelClass}>
          <input
            type="checkbox"
            checked={sendAsPrivate()}
            onChange={e => setSendAsPrivate(e.currentTarget.checked)}
          />
          {LL().comments.sendAsPrivate()}
        </label>
        <Show when={commentError()} keyed>
          {msg => <p class={commentErrorClass}>{msg}</p>}
        </Show>
        <button
          type="button"
          class={commentSubmitButtonClass}
          disabled={!commentText().trim() || createResult.fetching}
          onClick={handleSubmitComment}
        >
          {createResult.fetching
            ? LL().comments.publishing()
            : LL().comments.submit()}
        </button>
      </div>

      <Show
        when={threads().length > 0}
        fallback={<p class={commentEmptyClass}>{LL().comments.empty()}</p>}
      >
        <For each={threads()}>
          {threadRef => {
            const thread = createFragment(
              CommentThreadFragment,
              () => threadRef,
            )

            return (
              <div
                class={`${commentThreadClass}${thread().private ? ` ${commentPrivateThreadClass}` : ""}`}
              >
                <Show when={thread().private}>
                  <div class={commentPrivateHeaderClass}>
                    <span class={commentPrivateBadgeClass}>
                      <Icon name="mail-open" size="12" />
                      {LL().inbox.typePrivateEmail()}
                    </span>
                    <span class={commentPrivateDescriptionClass}>
                      {LL().comments.privateVisibility()}
                    </span>
                  </div>
                </Show>
                <For each={thread().messages}>
                  {(messageRef, index) => {
                    const message = createFragment(
                      CommentMessageFragment,
                      () => messageRef,
                    )
                    return (
                      <CommentCard
                        message={message}
                        isReply={index() > 0}
                        postAuthorId={postAuthorId()}
                        onDeleted={() =>
                          reexecuteComments({ requestPolicy: "network-only" })
                        }
                      />
                    )
                  }}
                </For>
                <button
                  type="button"
                  class={`${replyButtonClass}${thread().messages.length > 1 ? ` ${replyButtonIndentedClass}` : ""}`}
                  onClick={() => {
                    setReplyingTo(
                      replyingTo() === thread().id ? null : thread().id,
                    )
                    setReplyText("")
                  }}
                >
                  {LL().comments.reply()}
                </button>
                <Show when={replyingTo() === thread().id}>
                  <div class={commentFormClass}>
                    <textarea
                      class={commentTextareaClass}
                      placeholder={LL().comments.writeComment()}
                      rows="2"
                      value={replyText()}
                      onInput={e => setReplyText(e.currentTarget.value)}
                      ref={el => el.focus()}
                    />
                    <Show when={replyError()} keyed>
                      {msg => <p class={commentErrorClass}>{msg}</p>}
                    </Show>
                    <button
                      type="button"
                      class={commentSubmitButtonClass}
                      disabled={!replyText().trim() || replyResult.fetching}
                      onClick={() => handleSubmitReply(thread().id)}
                    >
                      {replyResult.fetching
                        ? LL().comments.publishing()
                        : LL().comments.reply()}
                    </button>
                  </div>
                </Show>
              </div>
            )
          }}
        </For>
      </Show>
    </div>
  )
}

function CommentCard(props: {
  message: () => {
    id: string
    content: string
    locale: string | null
    createdAt: unknown
    author: {
      id: string
      isMe: boolean
      authorName: string | null
      title: string | null
      username: string | null
      email: string | null
      profileImage: string | null
      publicationUrl: string | null
      publication: { id: string; sub: { id: string } | null } | null
    }
  }
  isReply: boolean
  postAuthorId: string | null
  onDeleted: () => void
}) {
  const { LL, locale } = useI18nContext()
  const location = useLocation()
  const m = createMemo(() => props.message())
  const relativeTime = useRelativeTime(() => m().createdAt as number)

  const [showTranslated, setShowTranslated] = createSignal(false)
  const [translatedContent, setTranslatedContent] = createSignal<string | null>(
    null,
  )
  const [translateState, executeTranslate] = createMutationWithErrorHandling(
    TranslateMessageMutation,
    "translateMessage",
  )
  const [deleteState, executeDelete] = createMutationWithErrorHandling(
    graphql(
      `mutation DeleteCommentMessage($messageId: String!) {
        deleteMessage(messageId: $messageId) {
          ... on MutationDeleteMessageSuccess {
            data
          }
          ... on BaseError {
            ...ErrorFragment
          }
        }
      }`,
      [ErrorFragment],
    ),
    "deleteMessage",
  )

  const canDelete = createMemo(() => m().author.isMe)

  const canTranslate = createMemo(
    () => m().locale != null && m().locale !== locale(),
  )

  async function handleTranslate() {
    if (showTranslated()) {
      setShowTranslated(false)
      return
    }

    if (translatedContent()) {
      setShowTranslated(true)
      return
    }

    const out = await executeTranslate(
      { messageId: m().id, toLocale: locale() },
      { muteGraphQlUnionToast: true },
    )

    if (out.success) {
      setTranslatedContent(out.data.translatedContent)
      setShowTranslated(true)
    }
  }

  async function handleDelete() {
    if (!window.confirm(LL().comments.deleteConfirm())) return

    const out = await executeDelete(
      { messageId: m().id },
      { muteGraphQlUnionToast: true },
    )

    if (out.success) props.onDeleted()
  }

  const isPostAuthor = createMemo(() => m().author.id === props.postAuthorId)
  const isSubscribed = createMemo(
    () => !m().author.isMe && !!m().author.publication?.sub,
  )
  const subscribeUrl = createMemo(() => {
    const { username, id } = m().author
    if (!username) return null
    return `/subscribe?publicationId=${id}&name=${encodeURIComponent(username)}&returnTo=${encodeURIComponent(location.pathname)}`
  })
  const canSubscribe = createMemo(
    () => !m().author.isMe && !isSubscribed() && subscribeUrl() !== null,
  )

  return (
    <div
      class={`${commentCardClass}${props.isReply ? ` ${commentReplyClass}` : ""}`}
    >
      <div class={commentHeaderClass}>
        <img
          src={
            m().author.profileImage
              ? cdnClass(m().author.profileImage!, "smallThumb")
              : getDefaultAvatar(m().author.id)
          }
          alt={displayName(m().author) ?? ""}
          class={commentAvatarClass}
        />
        <div class={commentAuthorInfoClass}>
          <span class={commentAuthorNameClass}>
            {m().author.isMe
              ? LL().inbox.you()
              : (displayName(m().author) ??
                LL().comments.anonymousParticipant())}
            <Show when={isPostAuthor()}>
              <span class={commentAuthorBadgeClass}>
                {LL().comments.author()}
              </span>
            </Show>
          </span>
          <Show when={!m().author.isMe && m().author.publicationUrl}>
            <a
              href={m().author.publicationUrl!}
              target="_blank"
              rel="noopener noreferrer"
              class={commentPublicationLinkClass}
            >
              {new URL(m().author.publicationUrl!).host}
            </a>
          </Show>
        </div>
        <Show when={canSubscribe()}>
          <a href={subscribeUrl()!} class={commentFollowLinkClass}>
            <Icon name="plus" size="12" />
            {LL().subscribe.button()}
          </a>
        </Show>
        <Show when={isSubscribed()}>
          <span class={commentSubscribedLabelClass}>
            {LL().subscriptions.subscribed()}
          </span>
        </Show>
      </div>
      <div class={commentContentClass}>
        <RichContent
          content={showTranslated() ? translatedContent()! : m().content}
        />
      </div>
      <div class={commentFooterClass}>
        <div class={commentFooterActionsClass}>
          <Show when={canTranslate()}>
            <button
              type="button"
              class={commentTranslateButtonClass}
              disabled={translateState.fetching}
              onClick={handleTranslate}
            >
              {translateState.fetching
                ? LL().common.translating()
                : showTranslated()
                  ? LL().comments.showOriginal({
                      locale: getLocaleDisplayName(m().locale),
                    })
                  : LL().comments.translateTo({
                      locale: getLanguageLabel(locale()),
                    })}
            </button>
          </Show>
          <Show when={canDelete()}>
            <button
              type="button"
              class={commentDeleteButtonClass}
              disabled={deleteState.fetching}
              onClick={handleDelete}
            >
              <Icon name="trash-2" size="12" />
              {deleteState.fetching
                ? LL().comments.deleting()
                : LL().comments.delete()}
            </button>
          </Show>
        </div>
        <span class={commentDateClass}>{relativeTime()}</span>
      </div>
    </div>
  )
}
